import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta } from '../../acesso';
import { Icone } from '../../design/Icone';
import {
  colocarNoParadao,
  lancarLoteDoProduto,
  definirPmcDoCadastro,
  detalharProduto,
  lerReais,
  paraCampo,
  reais,
  reaisCurto,
  salvarVitamina,
} from '../../produtos';
import type { DetalheDoProduto, ProblemaDaConfiguracao, VersaoDaVitamina } from '../../produtos';
import { mensagemDeFalha, useEscape } from '../equipe/Equipe';
import { PremioNaBase } from './PremioNaBase';
import { useValoresOcultos } from '../../design/sigilo';

/**
 * TELA 28-B — DETALHE DO PRODUTO, FAIXAS E HISTÓRICO.
 *
 * "O drawer separa o que vem de fora do que o InovaPay configura." Em cima, os dados do
 * relatório (somente leitura, com a fonte e a hora). Embaixo, a camada comercial: programa e
 * faixas. Salvar cria uma VERSÃO NOVA — o resumo diz o que muda antes do botão, e a anterior
 * fica no histórico. Nunca "Salvo!" antes da resposta do servidor.
 */

const MAXIMO_DE_FAIXAS = 2;

type FaixaEditada = { readonly chave: number; readonly preco: string; readonly premio: string };

type EstadoDoSalvar =
  | { readonly fase: 'parado' }
  | { readonly fase: 'salvando' }
  | { readonly fase: 'salvo'; readonly versao: number; readonly hora: string }
  | { readonly fase: 'recusado'; readonly problemas: readonly ProblemaDaConfiguracao[] }
  | { readonly fase: 'conflito' }
  | { readonly fase: 'erro'; readonly mensagem: string };

let proximaChave = 1;
const faixasDaVersao = (v: VersaoDaVitamina | null): FaixaEditada[] =>
  (v?.faixas ?? []).map((f) => ({
    chave: proximaChave++,
    preco: paraCampo(f.precoMinimo),
    // "só números": o prêmio vem nulo e a coluna nem aparece
    premio: f.premio === null ? '' : paraCampo(f.premio),
  }));

export function DrawerDoProduto({
  id,
  sessao,
  configura,
  aoFechar,
  aoSalvar,
}: {
  id: string;
  sessao: Sessao;
  configura: boolean;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const ocultos = useValoresOcultos();
  const [produto, setProduto] = useState<DetalheDoProduto | null>(null);
  const [falhaAoCarregar, setFalhaAoCarregar] = useState<string | null>(null);
  const [ativa, setAtiva] = useState(false);
  const [faixas, setFaixas] = useState<FaixaEditada[]>([]);
  const [salvar, setSalvar] = useState<EstadoDoSalvar>({ fase: 'parado' });
  const [confirmarSaida, setConfirmarSaida] = useState(false);

  const carregar = useCallback(async () => {
    setFalhaAoCarregar(null);
    const r = await detalharProduto(id);
    if (!r.ok) return setFalhaAoCarregar(mensagemDeFalha(r.falha));
    setProduto(r.dados);
    setAtiva(r.dados.vitamina?.ativa ?? false);
    setFaixas(faixasDaVersao(r.dados.vitamina));
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const atual = produto?.vitamina ?? null;
  const mudancas = useMemo(() => resumo(atual, ativa, faixas), [atual, ativa, faixas]);
  const pendente = mudancas.length > 0;
  const fechar = () => {
    // Sair com alteração pendente pede confirmação — dentro do drawer, sem janela do navegador.
    if (pendente && salvar.fase !== 'salvando') return setConfirmarSaida(true);
    aoFechar();
  };
  useEscape(fechar, true);

  async function enviar() {
    if (!produto || salvar.fase === 'salvando') return;
    const lidas = faixas.map((f) => ({
      precoMinimo: lerReais(f.preco),
      premio: lerReais(f.premio),
    }));
    const invalida = lidas.findIndex((f) => f.precoMinimo === null || f.premio === null);
    if (invalida >= 0) {
      setSalvar({
        fase: 'recusado',
        problemas: [
          {
            nivel: 'erro',
            codigo: 'valor_invalido',
            faixa: invalida + 1,
            mensagem: `Faixa ${String(invalida + 1)}: digite o valor em reais (ex.: 9,90).`,
          },
        ],
      });
      return;
    }
    setSalvar({ fase: 'salvando' });
    const r = await salvarVitamina(sessao, produto.id, {
      ativa,
      faixas: lidas.map((f) => ({ precoMinimo: f.precoMinimo ?? '', premio: f.premio ?? '' })),
      versaoEsperada: atual?.versao ?? 0,
    });
    if (r.ok) {
      setSalvar({
        fase: 'salvo',
        versao: r.dados.versao,
        hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      });
      aoSalvar();
      await carregar();
      return;
    }
    if (r.falha.tipo === 'recusada')
      return setSalvar({ fase: 'recusado', problemas: r.falha.problemas });
    if (r.falha.tipo === 'conflito') return setSalvar({ fase: 'conflito' });
    setSalvar({ fase: 'erro', mensagem: mensagemDeFalha(r.falha) });
  }

  const problemasDa = (n: number) =>
    salvar.fase === 'recusado' ? salvar.problemas.filter((p) => p.faixa === n) : [];

  return (
    <div className="ac-veu" onClick={fechar} role="presentation">
      <aside
        className="ac-drawer pd-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={produto?.descricao ?? 'Produto'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-drawer-cabeca">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-drawer-nome">{produto?.descricao ?? 'Carregando…'}</div>
            {produto && (
              <div className="pd-drawer-codigos">
                {[
                  produto.codigo,
                  produto.ean ? `EAN ${produto.ean}` : null,
                  produto.fabricante,
                  produto.grupo,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          <button type="button" className="ac-fechar" onClick={fechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.8} />
          </button>
        </div>

        {confirmarSaida && (
          <div className="pd-sair" role="alertdialog" aria-label="Alterações não salvas">
            <Icone nome="warn" tamanho={16} traco={1.7} />
            <span>Você tem alterações não salvas nas faixas.</span>
            <span className="ac-espaco" />
            <button type="button" className="ac-botao" onClick={() => setConfirmarSaida(false)}>
              Continuar editando
            </button>
            <button type="button" className="ac-botao ac-botao-perigo" onClick={aoFechar}>
              Descartar e fechar
            </button>
          </div>
        )}

        {falhaAoCarregar && (
          <div className="ac-secao">
            <div className="ac-erro" role="alert">
              <div className="ac-erro-titulo">Não foi possível carregar o produto</div>
              <div className="ac-erro-texto">{falhaAoCarregar}</div>
              <button type="button" className="ac-botao" onClick={() => void carregar()}>
                <Icone nome="retry" tamanho={14} traco={1.7} />
                Tentar de novo
              </button>
            </div>
          </div>
        )}

        {produto && (
          <>
            <div className="ac-secao pd-leitura">
              <div className="pd-leitura-cabeca">
                <Icone nome="file" tamanho={15} traco={1.6} />
                <div className="ac-rotulo-mono" style={{ margin: 0 }}>
                  DADOS DO PRODUTO · SOMENTE LEITURA
                </div>
                <span className="ac-espaco" />
                <span className="pd-fonte-drawer">
                  do HOS
                  {produto.vistoNaOrigemEm
                    ? ` · lido ${dataHoraCurta(produto.vistoNaOrigemEm)}`
                    : ''}
                </span>
              </div>
              <div className="pd-leitura-grade">
                <Dado rotulo="CÓDIGO INTERNO" valor={produto.codigo ?? '—'} />
                <Dado rotulo="EAN" valor={produto.ean ?? '—'} />
                <Dado rotulo="LABORATÓRIO" valor={produto.fabricante ?? '—'} />
                <Dado rotulo="GRUPO" valor={produto.grupo ?? '—'} />
                <Dado rotulo="CLASSIFICAÇÃO" valor={produto.classificacao ?? '—'} />
                <Dado rotulo="ESTOQUE" valor={produto.estoque ?? '—'} />
                <Dado rotulo="CUSTO" valor={produto.custo ? reais(produto.custo) : '—'} />
                <Dado
                  rotulo="PREÇO BASE"
                  valor={produto.precoBase ? reais(produto.precoBase) : '—'}
                />
                <Dado
                  rotulo="PMC DO HOS · TETO DE VENDA"
                  valor={produto.pmc ? reais(produto.pmc) : '—'}
                />
              </div>
            </div>

            <PmcDoCadastro
              produtoId={produto.id}
              atual={produto.pmcCadastro}
              sessao={sessao}
              configura={configura}
            />

            <div className="ac-secao">
              <div className="ac-rotulo-mono ac-rotulo-mono-marca" style={{ marginBottom: 11 }}>
                CONFIGURAÇÃO INOVAPAY
              </div>
              <div className="pd-opcao">
                <div style={{ flex: 1 }}>
                  <div className="pd-opcao-titulo">Programa comercial</div>
                  <div className="pd-opcao-texto">
                    Classificação administrada aqui · pré-vencido e paradão não entram: são
                    condições calculadas
                  </div>
                </div>
                <div className="pd-escolha" role="radiogroup" aria-label="Programa comercial">
                  {(
                    [
                      [true, 'Vitamina'],
                      [false, 'Nenhum'],
                    ] as const
                  ).map(([valor, rotulo]) => (
                    <button
                      key={rotulo}
                      type="button"
                      role="radio"
                      aria-checked={ativa === valor}
                      disabled={!configura}
                      className={`ac-chip${ativa === valor ? ' ac-chip-ativo' : ''}`}
                      onClick={() => {
                        setAtiva(valor);
                        if (valor && faixas.length === 0)
                          setFaixas([{ chave: proximaChave++, preco: '', premio: '' }]);
                        setSalvar({ fase: 'parado' });
                      }}
                    >
                      {rotulo}
                    </button>
                  ))}
                </div>
              </div>
              {!ocultos && <PremioNaBase id={produto.id} sessao={sessao} configura={configura} />}
              <ParadaoEPreVencido
                produtoId={produto.id}
                noParadao={produto.noParadao}
                lotesAtivos={produto.lotesAtivos}
                sessao={sessao}
                configura={configura}
                aoMudar={() => {
                  void carregar();
                  aoSalvar();
                }}
              />
            </div>

            {ativa && (
              <div className="ac-secao">
                <div className="ac-secao-cabeca">
                  <div className="ac-rotulo-mono ac-rotulo-mono-marca">
                    FAIXAS DE VENDA E PREMIAÇÃO
                  </div>
                  <span className="ac-espaco" />
                  <span className="ac-nota" style={{ fontSize: 11 }}>
                    lista configurável · ordem validada pelo servidor
                  </span>
                </div>
                <div className={`pd-faixas${ocultos ? ' pd-faixas-sem-premio' : ''}`}>
                  <div className="pd-faixas-cabeca">
                    <div>#</div>
                    <div>VENDER A PARTIR DE</div>
                    {!ocultos && <div>PREMIAÇÃO</div>}
                    <div>VIGÊNCIA</div>
                    <div />
                  </div>
                  {faixas.map((f, i) => {
                    const gravada = atual?.ativa ? atual.faixas[i] : undefined;
                    const mudouPreco = !gravada || lerReais(f.preco) !== gravada.precoMinimo;
                    const mudouPremio = !gravada || lerReais(f.premio) !== gravada.premio;
                    const mudou = mudouPreco || mudouPremio;
                    const erro = problemasDa(i + 1).some((p) => p.nivel === 'erro');
                    return (
                      <div key={f.chave} className={`pd-faixa${mudou ? ' pd-faixa-mudou' : ''}`}>
                        <div className="pd-faixa-n">{i + 1}</div>
                        <label
                          className={`pd-campo${mudouPreco ? ' pd-campo-mudou' : ''}${erro ? ' pd-campo-erro' : ''}`}
                        >
                          <span>R$</span>
                          <input
                            value={f.preco}
                            inputMode="decimal"
                            disabled={!configura}
                            aria-label={`Faixa ${String(i + 1)}: vender a partir de`}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFaixas((l) =>
                                l.map((x) => (x.chave === f.chave ? { ...x, preco: v } : x)),
                              );
                              setSalvar({ fase: 'parado' });
                            }}
                          />
                        </label>
                        {!ocultos && (
                          <label
                            className={`pd-campo${mudouPremio ? ' pd-campo-mudou' : ''}${erro ? ' pd-campo-erro' : ''}`}
                          >
                            <span>R$</span>
                            <input
                              value={f.premio}
                              inputMode="decimal"
                              disabled={!configura}
                              aria-label={`Faixa ${String(i + 1)}: premiação`}
                              onChange={(e) => {
                                const v = e.target.value;
                                setFaixas((l) =>
                                  l.map((x) => (x.chave === f.chave ? { ...x, premio: v } : x)),
                                );
                                setSalvar({ fase: 'parado' });
                              }}
                            />
                          </label>
                        )}
                        <div className="pd-faixa-vigencia">
                          {gravada && !mudou && atual
                            ? `desde ${dataHoraCurta(atual.criadoEm).slice(0, 5)}`
                            : 'nova versão'}
                        </div>
                        <div>
                          {configura && (
                            <button
                              type="button"
                              className="pd-remover"
                              aria-label={`Remover faixa ${String(i + 1)}`}
                              onClick={() => {
                                setFaixas((l) => l.filter((x) => x.chave !== f.chave));
                                setSalvar({ fase: 'parado' });
                              }}
                            >
                              <Icone nome="x" tamanho={14} traco={1.8} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="pd-faixas-pe">
                    {configura && faixas.length < MAXIMO_DE_FAIXAS && (
                      <button
                        type="button"
                        className="pd-adicionar"
                        onClick={() =>
                          setFaixas((l) => [...l, { chave: proximaChave++, preco: '', premio: '' }])
                        }
                      >
                        <Icone nome="plus" tamanho={14} traco={1.8} />
                        Adicionar faixa
                      </button>
                    )}
                    <span className="ac-espaco" />
                    <span className="ac-nota">
                      até {MAXIMO_DE_FAIXAS} faixas · remover tira da <b>nova versão</b>, nunca das
                      anteriores
                    </span>
                  </div>
                </div>
                {salvar.fase === 'recusado' && (
                  <div className="pd-recusa" role="alert">
                    <Icone nome="warn" tamanho={16} traco={1.7} />
                    <div>
                      <b>O servidor recusou:</b>{' '}
                      {salvar.problemas
                        .filter((p) => p.nivel === 'erro')
                        .map((p) => p.mensagem)
                        .join(' ')}
                      <div className="pd-recusa-nota">
                        Validação vinda da resposta do servidor — a tela não decide limites.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {configura && pendente && (
              <div className="ac-secao">
                <div className="ac-secao-cabeca">
                  <div className="ac-rotulo-mono ac-rotulo-mono-marca">RESUMO DA ALTERAÇÃO</div>
                  <span className="ac-espaco" />
                  <span className="ac-nota" style={{ fontSize: 11 }}>
                    nova versão · a anterior fica no histórico
                  </span>
                </div>
                <div className="pd-resumo">
                  {mudancas.map((m) => (
                    <div key={m.rotulo} className="pd-resumo-linha">
                      <span className="pd-resumo-rotulo">{m.rotulo}</span>
                      {m.de !== null && <span className="pd-resumo-de">{m.de}</span>}
                      {m.de !== null && <Icone nome="chev-r" tamanho={13} traco={1.8} />}
                      <span className="pd-resumo-para">{m.para}</span>
                    </div>
                  ))}
                  <div className="pd-pe pd-resumo-pe">
                    Vale a partir da confirmação. Vendas já registradas continuam ligadas à versão
                    anterior.
                  </div>
                </div>
              </div>
            )}

            {configura && (pendente || salvar.fase !== 'parado') && (
              <div className="ac-secao pd-acoes">
                <button
                  type="button"
                  className="ac-botao ac-botao-primario pd-salvar"
                  disabled={salvar.fase === 'salvando' || !pendente}
                  onClick={() => void enviar()}
                >
                  {salvar.fase === 'salvando' ? 'Salvando…' : 'Salvar nova versão'}
                </button>
                {pendente && (
                  <button
                    type="button"
                    className="ac-botao pd-descartar"
                    disabled={salvar.fase === 'salvando'}
                    onClick={() => {
                      setAtiva(atual?.ativa ?? false);
                      setFaixas(faixasDaVersao(atual));
                      setSalvar({ fase: 'parado' });
                    }}
                  >
                    Descartar
                  </button>
                )}
                <span className="ac-espaco" />
                <EstadoDoSalvamento
                  estado={salvar}
                  pendente={pendente}
                  aoRecarregar={() => void carregar().then(() => setSalvar({ fase: 'parado' }))}
                  aoTentar={() => void enviar()}
                />
              </div>
            )}

            <div className="ac-secao pd-historico" style={{ borderBottom: 0 }}>
              <div className="ac-rotulo-mono">HISTÓRICO DA CONFIGURAÇÃO</div>
              {produto.historico.length === 0 && (
                <div className="ac-pe-modal" style={{ marginTop: 0 }}>
                  Nenhuma versão ainda: este produto nunca teve configuração de vitamina.
                </div>
              )}
              {produto.historico.map((v, i) => (
                <div key={v.versao} className="ac-historico">
                  <span className="ac-historico-hora">{dataHoraCurta(v.criadoEm)}</span>
                  <div style={{ flex: 1 }}>
                    <b>{v.criadoPor ?? 'Migração do 1.0'}</b>{' '}
                    {descreverVersao(v, produto.historico[i + 1] ?? null, ocultos)}{' '}
                    <span className="ac-fraco">(v{v.versao})</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function EstadoDoSalvamento({
  estado,
  pendente,
  aoRecarregar,
  aoTentar,
}: {
  estado: EstadoDoSalvar;
  pendente: boolean;
  aoRecarregar: () => void;
  aoTentar: () => void;
}) {
  if (estado.fase === 'salvo' && !pendente)
    return (
      <span className="pd-estado pd-estado-ok" role="status">
        <Icone nome="check" tamanho={14} traco={2.2} />
        Salvo · v{estado.versao} em vigor <span className="ac-fraco">{estado.hora}</span>
      </span>
    );
  if (estado.fase === 'conflito')
    return (
      <span className="pd-estado pd-estado-info" role="alert">
        Esta configuração foi alterada por outro usuário
        <button type="button" className="ac-link" onClick={aoRecarregar}>
          Recarregar
        </button>
      </span>
    );
  if (estado.fase === 'erro')
    return (
      <span className="pd-estado pd-estado-erro" role="alert">
        <Icone nome="warn" tamanho={14} traco={1.8} />
        Não foi possível salvar
        <button type="button" className="ac-link" onClick={aoTentar}>
          Tentar
        </button>
      </span>
    );
  if (pendente)
    return (
      <span className="pd-estado">
        <span className="pd-ponto" />
        Alterações não salvas
      </span>
    );
  return null;
}

function Dado({ rotulo, valor, alerta }: { rotulo: string; valor: string; alerta?: boolean }) {
  return (
    <div>
      <div className="pd-dado-rotulo">{rotulo}</div>
      <div className={`pd-dado-valor${alerta ? ' pd-indisponivel' : ''}`}>{valor}</div>
    </div>
  );
}

type Mudanca = { readonly rotulo: string; readonly de: string | null; readonly para: string };

/** O que a nova versão muda em relação à vigente — o que aparece no resumo antes do botão. */
function resumo(
  atual: VersaoDaVitamina | null,
  ativa: boolean,
  faixas: readonly FaixaEditada[],
): Mudanca[] {
  const m: Mudanca[] = [];
  const estavaAtiva = atual?.ativa ?? false;
  if (ativa !== estavaAtiva)
    m.push({
      rotulo: 'Programa',
      de: estavaAtiva ? 'Vitamina' : 'Nenhum',
      para: ativa ? 'Vitamina' : 'Nenhum',
    });
  if (!ativa) return m;
  const antes = estavaAtiva ? (atual?.faixas ?? []) : [];
  const n = Math.max(antes.length, faixas.length);
  for (let i = 0; i < n; i += 1) {
    const a = antes[i];
    const f = faixas[i];
    const rot = `Faixa ${String(i + 1)}`;
    if (a && !f) {
      m.push({
        rotulo: `${rot} · removida`,
        de: `${reais(a.precoMinimo)} · ${reaisCurto(a.premio)}`,
        para: '—',
      });
      continue;
    }
    if (!f) continue;
    const preco = lerReais(f.preco);
    const premio = lerReais(f.premio);
    if (!a) {
      m.push({
        rotulo: `${rot} · nova`,
        de: null,
        para: `${f.preco ? `R$ ${f.preco}` : '—'} · ${f.premio ? `R$ ${f.premio}` : '—'}`,
      });
      continue;
    }
    if (preco !== a.precoMinimo)
      m.push({
        rotulo: `${rot} · vender a partir de`,
        de: reais(a.precoMinimo),
        para: preco ? reais(preco) : f.preco || '—',
      });
    if (premio !== a.premio)
      m.push({
        rotulo: `${rot} · premiação`,
        de: reais(a.premio),
        para: premio ? reais(premio) : f.premio || '—',
      });
  }
  return m;
}

/** Uma linha do histórico, em português, comparando com a versão anterior. */
function descreverVersao(
  v: VersaoDaVitamina,
  anterior: VersaoDaVitamina | null,
  ocultos: boolean,
): string {
  if (!v.ativa)
    return anterior?.ativa
      ? 'desativou a configuração de vitamina'
      : 'registrou o produto sem vitamina';
  if (!anterior || !anterior.ativa)
    return `ativou a configuração de vitamina com ${String(v.faixas.length)} ${v.faixas.length === 1 ? 'faixa' : 'faixas'}`;
  const partes: string[] = [];
  const n = Math.max(v.faixas.length, anterior.faixas.length);
  for (let i = 0; i < n; i += 1) {
    const a = anterior.faixas[i];
    const b = v.faixas[i];
    if (a && !b) partes.push(`removeu a faixa ${String(i + 1)}`);
    else if (!a && b)
      partes.push(
        `incluiu a faixa ${String(i + 1)} · ${reais(b.precoMinimo)}${ocultos ? '' : ` → ${reaisCurto(b.premio)}`}`,
      );
    else if (a && b) {
      const mud: string[] = [];
      if (a.precoMinimo !== b.precoMinimo)
        mud.push(`${reais(a.precoMinimo)} → ${reais(b.precoMinimo)}`);
      if (!ocultos && a.premio !== b.premio)
        mud.push(`premiação ${reaisCurto(a.premio)} → ${reaisCurto(b.premio)}`);
      if (mud.length) partes.push(`alterou faixa ${String(i + 1)} · ${mud.join(' e ')}`);
    }
  }
  return partes.join('; ') || 'salvou a configuração sem mudar as faixas';
}

/**
 * PMC DO CADASTRO — teto só quando o HOS NÃO tem PMC (dono, 28/09: o teto é o PMC do HOS).
 * Sem PMC aqui, não há teto. Cada mudança vai para a trilha com o motivo.
 */
function PmcDoCadastro({
  produtoId,
  atual,
  sessao,
  configura,
}: {
  produtoId: string;
  atual: string | null;
  sessao: Sessao;
  configura: boolean;
}) {
  const [vigente, setVigente] = useState(atual);
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(atual ? atual.replace('.', ',') : '');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const salvar = async (tirar = false) => {
    setEnviando(true);
    setMsg(null);
    const r = await definirPmcDoCadastro(
      sessao,
      produtoId,
      tirar ? null : valor.trim(),
      motivo.trim() || (tirar ? 'teto retirado' : ''),
    );
    setEnviando(false);
    if (!r.ok) return setMsg(r.mensagem);
    setVigente(r.pmc);
    setEditando(false);
    setMotivo('');
    setMsg(r.pmc ? 'PMC salvo — vale como teto a partir de agora.' : 'Teto retirado.');
  };
  return (
    <div className="ac-secao">
      <div className="ac-secao-cabeca">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">
          PMC DO CADASTRO · SÓ QUANDO O HOS NÃO TEM PMC
        </div>
        <span className="ac-espaco" />
        {configura && !editando && (
          <button type="button" className="ac-botao" onClick={() => setEditando(true)}>
            {vigente ? 'Alterar' : 'Definir PMC'}
          </button>
        )}
      </div>
      {!editando ? (
        <p className="pd-pmc-texto">
          {vigente ? (
            <>
              <b>{reais(vigente)}</b> — nenhuma venda passa deste preço.
            </>
          ) : (
            'Sem PMC cadastrado: não há teto. O PMC do HOS não é usado (costuma ser o próprio preço de venda).'
          )}
        </p>
      ) : (
        <div className="pd-pmc-form">
          <label>
            <span>PMC (R$)</span>
            <input
              inputMode="decimal"
              value={valor}
              placeholder="39,90"
              onChange={(e) => setValor(e.target.value)}
            />
          </label>
          <label className="pd-pmc-motivo">
            <span>De onde veio?</span>
            <input
              value={motivo}
              maxLength={300}
              placeholder="Ex.: etiqueta da caixa, tabela CMED"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="pd-pmc-botoes">
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              disabled={enviando || !valor.trim() || motivo.trim().length < 3}
              onClick={() => void salvar()}
            >
              {enviando ? 'Salvando…' : 'Salvar PMC'}
            </button>
            {vigente && (
              <button
                type="button"
                className="ac-botao"
                disabled={enviando}
                onClick={() => void salvar(true)}
              >
                Tirar teto
              </button>
            )}
            <button type="button" className="ac-botao" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {msg && (
        <p className="pd-pmc-texto" role="status">
          {msg}
        </p>
      )}
    </div>
  );
}

/**
 * PARADÃO E PRÉ-VENCIDO DAQUI MESMO (dono, 28/09): colocar o produto no Paradão e lançar um lote
 * de pré-vencido sem sair da página de produtos. As regras (quem pode, lote duplicado, validade)
 * são as mesmas das telas do Paradão e dos Pré-vencidos — é o servidor que decide.
 */
function ParadaoEPreVencido({
  produtoId,
  noParadao,
  lotesAtivos,
  sessao,
  configura,
  aoMudar,
}: {
  produtoId: string;
  noParadao: boolean;
  lotesAtivos: number;
  sessao: Sessao;
  configura: boolean;
  aoMudar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [lote, setLote] = useState({ codigo: '', validade: '', quantidade: '' });
  const [enviando, setEnviando] = useState<'paradao' | 'lote' | null>(null);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const noParadaoAgora = async () => {
    setEnviando('paradao');
    setAviso(null);
    const r = await colocarNoParadao(sessao, produtoId, motivo.trim());
    setEnviando(null);
    if (r.ok) {
      setMotivo('');
      setAviso({ tom: 'ok', texto: 'Produto colocado no Paradão.' });
      aoMudar();
    } else setAviso({ tom: 'erro', texto: r.mensagem });
  };
  const lancar = async () => {
    setEnviando('lote');
    setAviso(null);
    const r = await lancarLoteDoProduto(sessao, produtoId, {
      codigo: lote.codigo.trim().toUpperCase() || null,
      validade: lote.validade,
      quantidade: lote.quantidade.trim() || null,
    });
    setEnviando(null);
    if (r.ok) {
      setLote({ codigo: '', validade: '', quantidade: '' });
      setAviso({ tom: 'ok', texto: 'Lote de pré-vencido lançado.' });
      aoMudar();
    } else setAviso({ tom: 'erro', texto: r.mensagem });
  };

  return (
    <div className="pd-programas-acoes">
      <div className="pd-opcao">
        <div style={{ flex: 1 }}>
          <div className="pd-opcao-titulo">Paradão</div>
          <div className="pd-opcao-texto">
            {noParadao ? 'Este produto está na lista do Paradão.' : 'Fora do Paradão.'}{' '}
            <a className="ac-link" href={`#/paradao?produto=${produtoId}`}>
              Ver o Paradão
            </a>
          </div>
        </div>
        {configura && !noParadao && (
          <div className="pd-acao-linha">
            <input
              className="pd-campo"
              placeholder="Motivo (opcional)"
              value={motivo}
              maxLength={300}
              onChange={(e) => setMotivo(e.target.value)}
            />
            <button
              type="button"
              className="ac-botao"
              disabled={enviando !== null}
              onClick={() => void noParadaoAgora()}
            >
              {enviando === 'paradao' ? 'Colocando…' : 'Colocar no Paradão'}
            </button>
          </div>
        )}
      </div>
      <div className="pd-opcao">
        <div style={{ flex: 1 }}>
          <div className="pd-opcao-titulo">Pré-vencido</div>
          <div className="pd-opcao-texto">
            {lotesAtivos > 0
              ? `${String(lotesAtivos)} lote${lotesAtivos > 1 ? 's' : ''} ativo${lotesAtivos > 1 ? 's' : ''}.`
              : 'Nenhum lote ativo.'}{' '}
            <a className="ac-link" href={`#/pre-vencidos?produto=${produtoId}`}>
              Ver os lotes
            </a>
          </div>
        </div>
        {configura && (
          <div className="pd-acao-linha">
            <input
              className="pd-campo"
              placeholder="Lote"
              value={lote.codigo}
              maxLength={30}
              onChange={(e) => setLote({ ...lote, codigo: e.target.value })}
              aria-label="Código do lote"
            />
            <input
              className="pd-campo"
              type="date"
              value={lote.validade}
              onChange={(e) => setLote({ ...lote, validade: e.target.value })}
              aria-label="Validade"
            />
            <input
              className="pd-campo pd-campo-curto"
              inputMode="numeric"
              placeholder="Qtd."
              value={lote.quantidade}
              onChange={(e) =>
                setLote({ ...lote, quantidade: e.target.value.replace(/[^0-9]/g, '') })
              }
              aria-label="Quantidade"
            />
            <button
              type="button"
              className="ac-botao"
              disabled={enviando !== null || !lote.validade}
              onClick={() => void lancar()}
            >
              {enviando === 'lote' ? 'Lançando…' : 'Lançar lote'}
            </button>
          </div>
        )}
      </div>
      {aviso && (
        <div className={aviso.tom === 'ok' ? 'pd-aviso-ok' : 'pd-aviso-erro'} role="status">
          {aviso.texto}
        </div>
      )}
    </div>
  );
}
